import React, { useState, useCallback, useEffect } from 'react';
import { Box, Drawer, CssBaseline, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Storefront,
  ChatCircleText,
  Users,
  Calendar,
  ClipboardText,
  Receipt,
  ClockUser,
  SignatureIcon,
} from '@phosphor-icons/react';
import { useAuth } from '../../context/AuthContext';
import { useBusiness } from '../../context/BusinessContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useNotifications } from '../../context/NotificationContext';
import { UnreadMessagesContext } from '../../context/UnreadMessagesContext';
import SidebarNavigation, { EXPANDED_WIDTH } from '../layout/SidebarNavigation';
import ProfileMenuPopup from '../layout/ProfileMenuPopup';
import VerificationBanner from './VerificationBanner';
import GhostLoginBanner from '../Admin/GhostLoginBanner';
import GetToKnowIdotooPanel from './GetToKnowIdotooPanel';
import PageHelp from './PageHelp';
import AppHeader from '../layout/AppHeader';
import { MobileNavShell } from '../layout/mobileNav';
import api from '../../utils/api';

const _drawerWidth = EXPANDED_WIDTH;

// Menu sections organized as per requirements
const menuSections = [
  {
    title: 'Overview',
    items: [
      { 
        title: 'Business Profile', 
        icon: <Storefront size={24} weight="regular" />, 
        path: '/supplier/profile',
        permissions: ['view_profile']
      },
      { 
        title: 'Messages', 
        icon: <ChatCircleText size={24} weight="regular" />, 
        path: '/supplier/messages',
        permissions: ['view_messages']
      },
      { 
        title: 'My Couples', 
        icon: <Users size={24} weight="regular" />, 
        path: '/supplier/crm',
        permissions: ['view_bookings'] // CRM is related to bookings
      },
      { 
        title: 'Forms', 
        icon: <ClipboardText size={24} weight="regular" />, 
        permissions: ['view_bookings'],
        children: [
          { title: 'Form submissions', path: '/supplier/forms/submissions', permissions: ['view_bookings'] },
          { title: 'Form builder', path: '/supplier/forms/builder', permissions: ['manage_bookings'] },
        ],
      },
      { 
        title: 'Bookings', 
        icon: <Calendar size={24} weight="regular" />, 
        permissions: ['view_bookings'],
        path: '/supplier/bookings'
      },
      {
        title: 'Agreements',
        icon: <SignatureIcon size={32} />,
        path: '/supplier/agreements',
        permissions: ['view_bookings']
      },
      { 
        title: 'Invoices', 
        icon: <Receipt size={24} weight="regular" />, 
        path: '/supplier/invoices',
        permissions: ['view_financials']
      },
      { 
        title: 'Schedules', 
        icon: <ClockUser size={24} weight="regular" />, 
        path: '/supplier/schedules',
        permissions: ['manage_availability']
      },
    ]
  },
  {
    title: 'Account',
    items: [
      // Lumi and Settings removed - moved to header
    ]
  }
];

function SupplierLayout({ children }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileMenuAnchorEl, setProfileMenuAnchorEl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { selectedBusiness, hasNoBusiness, loading } = useBusiness();
  const { getPermissions } = usePermissions();
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteAllNotifications,
    deleteNotification,
    fetchNotifications,
    fetchUnreadCount
  } = useNotifications();
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [emailUnreadCount, setEmailUnreadCount] = useState(0);
  const unreadMessagesCount = chatUnreadCount + emailUnreadCount;

  const businessId = selectedBusiness?.business?._id;
  const refreshUnreadMessagesCount = useCallback(async () => {
    if (!businessId) {
      setChatUnreadCount(0);
      setEmailUnreadCount(0);
      return;
    }
    try {
      const [conversationsResponse, mailThreadsResponse] = await Promise.all([
        api.get('/api/chat/conversations', { params: { businessId } }),
        api.get('/api/mail-sync/threads', { params: { businessId } }).catch(() => ({ data: { threads: [] } }))
      ]);
      const conversations = Array.isArray(conversationsResponse?.data) ? conversationsResponse.data : [];
      const chatTotal = conversations.reduce((sum, c) => sum + (c.unreadCounts?.supplier || 0), 0);
      const mailThreads = Array.isArray(mailThreadsResponse?.data?.threads) ? mailThreadsResponse.data.threads : [];
      const emailTotal = mailThreads.reduce((sum, thread) => sum + (thread.unread ? 1 : 0), 0);
      setChatUnreadCount(chatTotal);
      setEmailUnreadCount(emailTotal);
    } catch {
      setChatUnreadCount(0);
      setEmailUnreadCount(0);
    }
  }, [businessId]);

  useEffect(() => {
    refreshUnreadMessagesCount();
    const onVisibilityChange = () => { if (document.visibilityState === 'visible') refreshUnreadMessagesCount(); };
    document.addEventListener('visibilitychange', onVisibilityChange);
    const interval = setInterval(refreshUnreadMessagesCount, 60000);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      clearInterval(interval);
    };
  }, [refreshUnreadMessagesCount]);

  useEffect(() => {
    if (!businessId) return;
    fetchNotifications(1, 20, false, businessId);
    fetchUnreadCount(businessId);
    // `fetchNotifications`/`fetchUnreadCount` are context functions that are recreated
    // on render; only business changes should trigger a refresh here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  const unreadMessagesValue = React.useMemo(
    () => ({ unreadMessagesCount, chatUnreadCount, emailUnreadCount, refreshUnreadMessagesCount }),
    [unreadMessagesCount, chatUnreadCount, emailUnreadCount, refreshUnreadMessagesCount]
  );

  const currentDrawerWidth = EXPANDED_WIDTH;

  const _handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setProfileMenuAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/supplier/login');
  };

  // No collapse functionality - always icon-only

  const isLeadDetailPage = /\/crm\/leads\/[^/]+$/.test(location.pathname);

  const isActive = useCallback((path) => {
    if (!path) return false;
    // Special case: /supplier/profile redirects to /supplier/edit-profile
    if (path === '/supplier/profile') {
      return location.pathname === '/supplier/profile' || location.pathname === '/supplier/edit-profile';
    }
    if (path === '/supplier/forms/submissions') {
      return location.pathname === '/supplier/forms/submissions';
    }
    if (path === '/supplier/forms/builder') {
      return (
        location.pathname === '/supplier/forms/builder' ||
        location.pathname === '/supplier/forms/create' ||
        /^\/supplier\/forms\/edit\//.test(location.pathname)
      );
    }
    return location.pathname === path || location.pathname.startsWith(path);
  }, [location.pathname]);

  // Filter menu items based on user permissions
  const getFilteredMenuSections = useCallback(() => {
    const userPermissions = getPermissions();
    return menuSections.map(section => ({
      ...section,
      items: section.items
        .map(item => {
          if (!item.children || item.children.length === 0) {
            return item;
          }

          const filteredChildren = item.children.filter(child => {
            if (!child.permissions || child.permissions.length === 0) return true;
            return child.permissions.some(permission => userPermissions.includes(permission));
          });

          return {
            ...item,
            children: filteredChildren
          };
        })
        .filter(item => {
          if (!item.permissions || item.permissions.length === 0) return true;
          return item.permissions.some(permission => userPermissions.includes(permission));
        })
        .filter(item => !item.children || item.children.length > 0)
    })).filter(section => section.items.length > 0);
  }, [getPermissions]);

  const logoComponent = (
    <img 
      src={`${process.env.PUBLIC_URL}/img/logo.svg`}
      alt="Logo" 
      style={{ height: 30 }} 
    />
  );

  const logoIconComponent = (
    <img 
      src={`${process.env.PUBLIC_URL}/img/logoicon.svg`}
      alt="Logo Icon" 
      style={{ height: 30 }} 
    />
  );

  const profileData = user ? {
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    name: user.firstName || 'Supplier',
    avatar: selectedBusiness?.business?.media?.logo,
  } : null;

  // Mobile: use new MobileNavShell (top bar, bottom nav, menu drawer)
  if (isMobile && (!hasNoBusiness || loading)) {
    return (
      <UnreadMessagesContext.Provider value={unreadMessagesValue}>
      <MobileNavShell
        isSupplier={true}
        userProfile={profileData}
        notifications={notifications}
        unreadCount={unreadCount}
        unreadMessagesCount={unreadMessagesCount}
        onMarkAsRead={markAsRead}
        onMarkAllAsRead={markAllAsRead}
        onDeleteAllNotifications={deleteAllNotifications}
        onDeleteNotification={deleteNotification}
        onNotificationPageClick={() => navigate('/supplier/notifications')}
        onLogout={handleLogout}
        getPermissions={getPermissions}
      >
        <Box
          sx={{
            p: isLeadDetailPage ? { xs: 0, sm: 2 } : { xs: 1.5, sm: 2 },
            width: '100%',
            maxWidth: '100%',
          }}
        >
          <GhostLoginBanner />
          {children}
          <PageHelp />
        </Box>
        <VerificationBanner />
        <GetToKnowIdotooPanel />
      </MobileNavShell>
      </UnreadMessagesContext.Provider>
    );
  }

  return (
    <UnreadMessagesContext.Provider value={unreadMessagesValue}>
    <Box sx={{ display: 'flex', overflowX: 'hidden', width: '100%', maxWidth: '100vw' }}>
      <CssBaseline />
      
      {/* Navigation - Desktop only */}
      {(!hasNoBusiness || loading) && (
        <Box
          component="nav"
          sx={{ 
            width: { md: currentDrawerWidth }, 
            flexShrink: { md: 0 },
            position: 'fixed',
            height: '100vh',
            transition: 'width 0.3s ease-in-out',
          }}
        >
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': { 
                boxSizing: 'border-box', 
                width: `${currentDrawerWidth}px !important`,
                minWidth: `${currentDrawerWidth}px !important`,
                maxWidth: `${currentDrawerWidth}px !important`,
                position: 'fixed',
                height: '100vh',
                transition: 'width 0.3s ease-in-out',
                overflowX: 'hidden',
                backgroundColor: 'transparent', // No background
              },
            }}
            open
          >
            <SidebarNavigation
              sections={getFilteredMenuSections()}
              userProfile={profileData}
              onProfileMenuOpen={handleProfileMenuOpen}
              logoComponent={logoComponent}
              logoIconComponent={logoIconComponent}
              isActive={isActive}
              isSupplier={true}
              notifications={notifications}
              unreadCount={unreadCount}
              unreadMessagesCount={unreadMessagesCount}
              onMarkAsRead={markAsRead}
              onMarkAllAsRead={markAllAsRead}
              onDeleteAllNotifications={deleteAllNotifications}
              onDeleteNotification={deleteNotification}
            />
          </Drawer>
        </Box>
      )}
      
      {/* Profile Menu Popup - Using same component as couples for consistency */}
      <ProfileMenuPopup
        anchorEl={profileMenuAnchorEl}
        open={Boolean(profileMenuAnchorEl)}
        onClose={handleProfileMenuClose}
        userProfile={profileData}
        onLogout={handleLogout}
      />
      
      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: (hasNoBusiness && !loading) 
            ? '100%' 
            : { 
                xs: '100%',
                md: `calc(100vw - ${currentDrawerWidth}px)` 
              },
          maxWidth: '100%',
          marginLeft: (hasNoBusiness && !loading) ? 0 : { xs: 0, md: `${currentDrawerWidth}px` },
          marginRight: (hasNoBusiness && !loading) 
            ? 0 
            : { md: 0 },
          marginTop: (hasNoBusiness && !loading) ? 0 : { xs: '64px', md: 0 },
          bgcolor: 'transparent',
          minHeight: '100vh',
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflowX: 'hidden',
          transition: 'margin-left 0.3s ease-in-out, width 0.3s ease-in-out, margin-right 0.3s ease-in-out',
        }}
      >
        <GhostLoginBanner />
        {/* App Header */}
        {(!hasNoBusiness || loading) && (
          <AppHeader 
            isSupplier={true} 
            notifications={notifications}
            unreadCount={unreadCount}
            onNotificationClick={() => navigate('/supplier/notifications')}
          />
        )}
        
        <Box
          sx={{
            flex: 1,
            p: isLeadDetailPage ? { xs: 0, sm: 2, md: 3 } : { xs: 1.5, sm: 2, md: 3 }, // No padding on mobile for lead detail
            display: 'flex',
            justifyContent: 'center',
            pb: { xs: 10, sm: 10, md: 8 }, // More bottom padding on mobile for verification banner
            overflowX: 'hidden',
            width: '100%',
            maxWidth: '100%',
          }}
        >
            <Box
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', sm: '100%', md: '1200px', lg: '1400px' },
                mx: 'auto',
                overflowX: 'hidden',
              }}
            >
              {children}
              {(!hasNoBusiness || loading) && <PageHelp />}
            </Box>
        </Box>

        {/* Verification Banner - Show at bottom of all pages */}
        {(!hasNoBusiness || loading) && <VerificationBanner />}
      </Box>
    </Box>
    <GetToKnowIdotooPanel />
    </UnreadMessagesContext.Provider>
  );
}

export default SupplierLayout; 